(() => {
  const padTime = (value) => String(Math.max(0, value)).padStart(2, '0');

  class RimthinOfferCountdown extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.timer = this.querySelector('[data-offer-timer]');
      this.offer = this.closest('.rth-product-offer');
      this.liveRegion = this.querySelector('[data-offer-live]');
      this.expiredMessage = this.querySelector('[data-offer-expired]');
      this.lastAnnouncedMinute = null;

      if (!this.timer) return;

      const endTime = this.parseEndTime(this.timer.dataset.endTime);
      if (!Number.isFinite(endTime)) {
        this.handleInvalidDate();
        return;
      }

      this.endTime = endTime;
      this.update();
      if (this.endTime > Date.now()) {
        this.interval = window.setInterval(() => this.update(), 1000);
      }
    }

    disconnectedCallback() {
      if (this.interval) window.clearInterval(this.interval);
    }

    parseEndTime(rawValue) {
      if (!rawValue) return Number.NaN;
      const normalized = rawValue.trim().replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
      return Date.parse(normalized);
    }

    update() {
      const remaining = this.endTime - Date.now();
      if (remaining <= 0) {
        this.expire();
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.setValue('days', days);
      this.setValue('hours', hours);
      this.setValue('minutes', minutes);
      this.setValue('seconds', seconds);

      const dayPart = this.querySelector('[data-time-part="days"]');
      const daySeparator = this.querySelector('[data-day-separator]');
      const hideZeroDays = this.timer.dataset.hideZeroDays === 'true' && days === 0;
      if (dayPart) dayPart.hidden = hideZeroDays;
      if (daySeparator) daySeparator.hidden = hideZeroDays;

      const remainingMinutes = Math.floor(totalSeconds / 60);
      if (this.liveRegion && remainingMinutes !== this.lastAnnouncedMinute) {
        const label = this.timer.dataset.countdownLabel || 'Ends in';
        this.liveRegion.textContent = `${label} ${days}d ${padTime(hours)}h ${padTime(minutes)}m`;
        this.lastAnnouncedMinute = remainingMinutes;
      }
    }

    setValue(part, value) {
      const target = this.querySelector(`[data-time-value="${part}"]`);
      if (target) target.textContent = padTime(value);
    }

    expire() {
      if (this.interval) window.clearInterval(this.interval);
      this.setValue('days', 0);
      this.setValue('hours', 0);
      this.setValue('minutes', 0);
      this.setValue('seconds', 0);

      const behavior = this.dataset.expiredBehavior || 'show_message';
      if (behavior === 'hide_offer' && this.offer) {
        this.offer.hidden = true;
        return;
      }

      if (behavior === 'hide_timer') {
        this.hidden = true;
        return;
      }

      if (this.timer) this.timer.hidden = true;
      if (this.expiredMessage) this.expiredMessage.hidden = false;
      if (this.liveRegion) this.liveRegion.textContent = this.expiredMessage?.textContent || 'Offer ended';
    }

    handleInvalidDate() {
      this.hidden = true;
      if (window.Shopify?.designMode) {
        this.hidden = false;
        if (this.timer) this.timer.hidden = true;
        if (this.expiredMessage) {
          this.expiredMessage.hidden = false;
          this.expiredMessage.textContent = 'Set a valid ISO 8601 countdown date in this block.';
        }
      }
    }
  }

  class RimthinOfferCode extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.button = this.querySelector('[data-copy-button]');
      this.codeElement = this.querySelector('[data-discount-code]');
      this.status = this.querySelector('[data-copy-status]');
      if (!this.button || !this.codeElement) return;

      this.handleClick = () => this.copyCode();
      this.button.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      if (this.button && this.handleClick) {
        this.button.removeEventListener('click', this.handleClick);
      }
      if (this.resetTimer) window.clearTimeout(this.resetTimer);
    }

    async copyCode() {
      if (this.copying) return;
      const code = this.codeElement.textContent.trim();
      if (!code) return;

      this.copying = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');

      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(code);
          copied = true;
        } else {
          copied = this.fallbackCopy(code);
        }
      } catch (error) {
        copied = this.fallbackCopy(code);
      }

      this.copying = false;
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');

      if (copied) {
        this.showSuccess();
      } else {
        this.showError();
      }
    }

    fallbackCopy(code) {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.insetInlineStart = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.select();

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      textArea.remove();
      return copied;
    }

    showSuccess() {
      const label = this.button.querySelector('[data-copy-label]');
      const copiedText = this.button.dataset.copiedText || 'Copied';
      const defaultText = this.button.dataset.defaultText || label?.textContent || 'Copy';

      this.button.dataset.copied = 'true';
      this.button.dataset.copyError = 'false';
      if (label) label.textContent = copiedText;
      if (this.status) this.status.textContent = copiedText;

      if (this.resetTimer) window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(() => {
        this.button.dataset.copied = 'false';
        if (label) label.textContent = defaultText;
      }, 2000);
    }

    showError() {
      const errorText = this.button.dataset.errorText || 'Copy failed. Select the code manually.';
      if (this.status) this.status.textContent = errorText;
      this.button.dataset.copied = 'false';
      this.button.dataset.copyError = 'true';

      if (this.resetTimer) window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(() => {
        this.button.dataset.copyError = 'false';
      }, 2000);

      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(this.codeElement);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  if (!customElements.get('rimthin-offer-countdown')) {
    customElements.define('rimthin-offer-countdown', RimthinOfferCountdown);
  }

  if (!customElements.get('rimthin-offer-code')) {
    customElements.define('rimthin-offer-code', RimthinOfferCode);
  }
})();
